import { useMemo, useState } from 'react';
import {
  Box, Typography, TextField, InputAdornment, Drawer, IconButton,
  alpha, useTheme, Stack, Collapse,
} from '@mui/material';
import { Search, Close, Menu, ExpandMore, FolderOpen } from '@mui/icons-material';
import { resolveMasterGroupIcon } from '../../constants/masters';
import type { MasterCatalogGroup } from '../../constants/masters';

const CATALOG_SEARCH_INPUT_PROPS = {
  startAdornment: (
    <InputAdornment position="start">
      <Search sx={{ fontSize: 17 }} color="action" />
    </InputAdornment>
  ),
};

interface Props {
  catalog: MasterCatalogGroup[];
  currentGroup: string;
  currentType: string;
  openGroups: Record<string, boolean>;
  onToggleGroup: (key: string) => void;
  onSelectType: (groupKey: string, typeKey: string) => void;
  totalTypes: number;
  accordionMode?: boolean;
  mobileOpen?: boolean;
  onMobileClose?: () => void;
  embedded?: boolean;
}

function CatalogContent({
  catalog, currentGroup, currentType, onSelectType, totalTypes,
  openGroups, onToggleGroup, accordionMode = false,
}: Omit<Props, 'mobileOpen' | 'onMobileClose' | 'embedded'>) {
  const theme = useTheme();
  const ink = theme.palette.primary.main;
  const [filter, setFilter] = useState('');

  const filteredCatalog = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return catalog;
    return catalog
      .map((group) => ({
        ...group,
        types: group.types.filter(
          (t) => t.label.toLowerCase().includes(q) || t.key.toLowerCase().includes(q)
            || group.label.toLowerCase().includes(q),
        ),
      }))
      .filter((g) => g.types.length > 0);
  }, [catalog, filter]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <Box
        sx={{
          px: 1.75,
          pt: 1.75,
          pb: 1.5,
          flexShrink: 0,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Stack direction="row" alignItems="baseline" justifyContent="space-between" sx={{ mb: 1.25 }}>
          <Typography sx={{ fontSize: '0.72rem', fontWeight: 750, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'text.secondary' }}>
            Catalog
          </Typography>
          <Typography sx={{ fontSize: '0.68rem', fontWeight: 650, color: 'text.disabled' }}>
            {totalTypes}
          </Typography>
        </Stack>
        <TextField
          fullWidth
          size="small"
          placeholder="Find a master…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          InputProps={CATALOG_SEARCH_INPUT_PROPS}
          sx={{
            '& .MuiOutlinedInput-root': {
              height: 36,
              borderRadius: 1.5,
              bgcolor: alpha('#0f172a', 0.03),
              fontSize: '0.8rem',
              '& fieldset': { borderColor: alpha('#0f172a', 0.08) },
              '&:hover fieldset': { borderColor: alpha('#0f172a', 0.16) },
              '&.Mui-focused fieldset': { borderColor: ink, borderWidth: 1 },
            },
          }}
        />
      </Box>

      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          px: 1.15,
          py: 1.25,
          scrollbarWidth: 'thin',
          '&::-webkit-scrollbar': { width: 6 },
          '&::-webkit-scrollbar-thumb': { bgcolor: alpha('#0f172a', 0.18), borderRadius: 6 },
          '&::-webkit-scrollbar-thumb:hover': { bgcolor: alpha('#0f172a', 0.28) },
        }}
      >
        {filteredCatalog.map((group) => {
          const isOpen = accordionMode ? !!openGroups[group.key] : true;

          return (
            <Box key={group.key} sx={{ mb: 1.35 }}>
              <Box
                onClick={accordionMode ? () => onToggleGroup(group.key) : undefined}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                  px: 1,
                  py: 0.45,
                  mb: 0.35,
                  userSelect: 'none',
                  cursor: accordionMode ? 'pointer' : 'default',
                }}
              >
                <Box sx={{ color: 'text.disabled', display: 'flex', '& svg': { fontSize: 14 } }}>
                  {resolveMasterGroupIcon(group.icon) || <FolderOpen sx={{ fontSize: 14 }} />}
                </Box>
                {group.label?.trim() && (
                <Typography
                  sx={{
                    flex: 1,
                    fontSize: '0.62rem',
                    fontWeight: 750,
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    color: 'text.disabled',
                    lineHeight: 1.2,
                  }}
                >
                  {group.label}
                </Typography>
                )}
                {accordionMode && (
                  <ExpandMore
                    sx={{
                      fontSize: 16,
                      color: 'text.disabled',
                      transform: isOpen ? 'rotate(180deg)' : 'none',
                      transition: 'transform 0.15s',
                    }}
                  />
                )}
              </Box>

              <Collapse in={isOpen} timeout="auto" unmountOnExit={accordionMode}>
                <Stack spacing={0.25}>
                  {group.types.map((t) => {
                    const selected = currentType === t.key && currentGroup === group.key;
                    if (!t.label?.trim()) return null;
                    return (
                      <Box
                        key={t.key}
                        component="button"
                        type="button"
                        onClick={() => onSelectType(group.key, t.key)}
                        sx={{
                          all: 'unset',
                          boxSizing: 'border-box',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          width: '100%',
                          minHeight: 36,
                          px: 1.15,
                          py: 0.7,
                          borderRadius: 1.5,
                          color: selected ? '#fff' : 'text.secondary',
                          bgcolor: selected ? ink : 'transparent',
                          fontWeight: selected ? 650 : 500,
                          fontSize: '0.8125rem',
                          letterSpacing: '-0.015em',
                          lineHeight: 1.25,
                          transition: 'background-color 0.14s ease, color 0.14s ease',
                          '&:hover': {
                            bgcolor: selected ? ink : alpha(theme.palette.text.primary, 0.05),
                            color: selected ? '#fff' : 'text.primary',
                          },
                        }}
                      >
                        <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {t.label}
                        </Box>
                      </Box>
                    );
                  })}
                </Stack>
              </Collapse>
            </Box>
          );
        })}

        {filteredCatalog.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ p: 2.5, textAlign: 'center', fontSize: '0.8125rem' }}>
            No types match
          </Typography>
        )}
      </Box>
    </Box>
  );
}

export function MasterCatalogDrawer({
  open, onClose, ...props
}: Props & { open: boolean; onClose: () => void }) {
  return (
    <Drawer
      anchor="left"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { width: { xs: 'min(300px, 90vw)', sm: 280 }, display: 'flex', flexDirection: 'column' },
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 0.75, borderBottom: 1, borderColor: 'divider' }}>
        <IconButton size="small" onClick={onClose} aria-label="Close catalog"><Close fontSize="small" /></IconButton>
      </Box>
      <CatalogContent
        catalog={props.catalog}
        currentGroup={props.currentGroup}
        currentType={props.currentType}
        onSelectType={props.onSelectType}
        totalTypes={props.totalTypes}
        openGroups={props.openGroups}
        onToggleGroup={props.onToggleGroup}
        accordionMode={props.accordionMode}
      />
    </Drawer>
  );
}

export function MasterCatalogSidebar(props: Omit<Props, 'mobileOpen' | 'onMobileClose'>) {
  return (
    <Box
      sx={{
        width: 260,
        minWidth: 260,
        flexShrink: 0,
        display: { xs: 'none', lg: 'flex' },
        flexDirection: 'column',
        alignSelf: 'stretch',
        minHeight: 0,
        bgcolor: alpha('#0f172a', 0.018),
        borderRight: 1,
        borderColor: 'divider',
      }}
    >
      <CatalogContent
        catalog={props.catalog}
        currentGroup={props.currentGroup}
        currentType={props.currentType}
        onSelectType={props.onSelectType}
        totalTypes={props.totalTypes}
        openGroups={props.openGroups}
        onToggleGroup={props.onToggleGroup}
        accordionMode={props.accordionMode}
      />
    </Box>
  );
}

export function MasterCatalogMobileTrigger({ onClick }: { onClick: () => void }) {
  return (
    <IconButton
      onClick={onClick}
      sx={{
        display: { lg: 'none' },
        flexShrink: 0,
        border: 1,
        borderColor: 'divider',
        borderRadius: 1.5,
        bgcolor: 'background.paper',
        width: 36,
        height: 36,
      }}
      aria-label="Open master catalog"
    >
      <Menu fontSize="small" />
    </IconButton>
  );
}

export default MasterCatalogSidebar;
